import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { ENV } from '@app/constants/global.constants';
import { Env } from '@app/types/env';
import { JengaError } from '@app/types/error';
import LoginDTO from '@data/models/user/login-dto';
import { Observable, of, throwError } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { UserService } from '@data/services/user.service';
import { SemDB } from '../../data/indexedDB/sem.db';
import { USER_LOGIN } from '@data/mocks/login.mock';
import { USERS } from '@data/mocks/users.mock';
import UserDTO from '@data/models/user/user-dto';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  public readonly LOGIN_PATH = '/api/users/login';

  private readonly ACCESS_TOKEN = 'access_token';
  private readonly USER_ID = 'user_id';
  private readonly USER_ROLE = 'user_role';
  private readonly USER_FULL_NAME = 'user_full_name';
  private readonly DATE_LOGIN = 'date_login';
  private semDB;

  constructor(
    @Inject(ENV) private env: Env,
    @Inject(DOCUMENT) private document: Document,
    private http: HttpClient,
    private userService: UserService
  ) {
    this.semDB = new SemDB();
  }

  public signIn(credentials: { login: string; password: string }): Observable<LoginDTO> {
    return this.http
      .post<LoginDTO>(`${this.env.apiBaseUrl}${this.LOGIN_PATH}`, credentials)
      .pipe(catchError((error) => throwError(error.error as JengaError)));
  }

  /**
   * Stores a token
   *
   * @param token token to be stored
   */
  setToken(token: string): void {
    localStorage.setItem(this.ACCESS_TOKEN, token);
  }

  /**
   * Retrieves the saved token
   *
   * @returns saved token
   */
  getToken(): string {
    return localStorage.getItem(this.ACCESS_TOKEN);
  }

  /**
   * Remove the saved token
   */
  removeToken(): void {
    localStorage.removeItem(this.ACCESS_TOKEN);
  }

  /**
   * Retrieves the userId
   *
   * @returns the userId
   */
  getUserId() {
    return localStorage.getItem(this.USER_ID);
  }

  /**
   * Remove the userId
   */
  removeUserId() {
    localStorage.removeItem(this.USER_ID);
  }

  /**
   * Retrieves the user full name
   *
   * @returns the userFullName
   */
  getUserFullName() {
    return localStorage.getItem(this.USER_FULL_NAME);
  }

  /**
   * Remove the userFullName
   */
  removeUserFullName() {
    localStorage.removeItem(this.USER_FULL_NAME);
  }

  /**
   * Retrieves the user full name
   *
   * @returns the DateLogin
   */
  getDateLogin() {
    return localStorage.getItem(this.DATE_LOGIN);
  }

  /**
   * Remove the DateLogin
   */
  removeDateLogin() {
    localStorage.removeItem(this.DATE_LOGIN);
  }

  /**
   * Retrieves the user role
   *
   * @returns the userRole
   */
  getUserRole(): 'ADMINISTRADOR' | 'VISUALITZADOR' | 'RESPONSABLE' {
    const role = localStorage.getItem(this.USER_ROLE) as 'ADMINISTRADOR' | 'VISUALITZADOR' | 'RESPONSABLE';
    if (role) {
      return role;
    }
    return 'VISUALITZADOR';
  }

  /**
   * Remove the userRole
   */
  removeUserRole() {
    localStorage.removeItem(this.USER_ROLE);
  }

  /**
   * Stores the Logged user
   *
   * @param loginData
   */
  setLoggedUser(loginData: LoginDTO): void {
    localStorage.setItem(this.ACCESS_TOKEN, loginData.access_token);
    localStorage.setItem(this.USER_ID, loginData.user.login);
    localStorage.setItem(this.USER_FULL_NAME, loginData.user.nom);
    localStorage.setItem(this.USER_ROLE, loginData.user.idRol);
    localStorage.setItem(this.DATE_LOGIN, loginData.date_login.toString());
    //Se guarda en IndexedDB
    this.semDB.login
      .put({
        access_token: this.getToken(),
        user_id: loginData.user.login,
        user_role: loginData.user.idRol,
        user_full_name: loginData.user.nom,
        date_login: loginData.date_login
      })
      .then(() => console.log('Datos almacenados en IndexedDB'))
      .catch((error) => console.error('Error al almacenar datos en IndexedDB', error));
  }

  setTokenData(loginData: LoginDTO): void {
    localStorage.setItem(this.ACCESS_TOKEN, loginData.access_token);
  }

  /**
   * Retrieves current logged user access_token / expires_in / user_id / user_full_name
   */
  getLoggedUser(): {
    access_token: string;
    user_id: string;
    user_full_name: string;
  } {
    return {
      [this.ACCESS_TOKEN]: this.getToken(),
      [this.USER_ID]: this.getUserId(),
      [this.USER_FULL_NAME]: this.getUserFullName()
    };
  }

  /**
   * Check if there is a logged in user
   */
  isUserLogged(): boolean {
    if (!this.getToken()) {
      //Intento sacarlo de la IndexedDB
      this.semDB.login
        .toArray()
        .then((datos) => {
          if (datos.length === 1) {
            const user = datos[0];
            localStorage.setItem(this.ACCESS_TOKEN, user.access_token);
            localStorage.setItem(this.USER_ID, user.user_id.toString());
            localStorage.setItem(this.USER_FULL_NAME, user.user_full_name);
            localStorage.setItem(this.USER_ROLE, user.user_role);
          } else {
            // console.log('No podemos recuperar el usuario de IndexedDB', datos);
            this.clearLoginSemDB();
          }
        })
        .catch((error) => console.error('Error al recuperar datos de IndexedDB', error));
    }
    return !!this.getToken();
  }

  /**
   * Log the current user out by removing credentials and log out from the system
   */
  logout(): void {
    this.removeToken();
    this.removeUserId();
    this.removeUserFullName();
    this.removeUserRole();
    this.clearLoginSemDB();
    this.userService.userLogged$.next(null);
  }

  clearLoginSemDB(): void {
    this.semDB.login
      .clear()
      .then(() => console.log('Tabla de login vaciada'))
      .catch((error) => console.error('Error al vaciar tabla de login', error));
  }
}
